/**
 * Gerenciador centralizado de abort para pipelines do CorelDRAW.
 * 
 * Padrão singleton que permite que qualquer componente sinalize
 * cancelamento de operações e outros componentes verifiquem o estado.
 * 
 * Features:
 * - Limpeza automática de operações antigas (previne memory leak)
 * - Fallback para memória se sessionStorage falhar
 * - TTL automático para sinais de abort
 */

import { storage } from '../../storage/storage';
import { sendToTerminal, terminalLog } from '../../terminalLogger';
import { ABORT_TTL } from './pipelineConstants';

export type AbortReason = 'document_closed' | 'user_cancelled' | 'timeout' | 'error';

interface AbortSignal {
  operationId: string;
  reason: AbortReason;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface AbortRef {
  aborted: boolean;
  reason?: AbortReason;
  registeredAt: number;
}

// Chave global para garantir singleton mesmo com HMR (Hot Module Reload)
const GLOBAL_KEY = '__ABORT_MANAGER_SINGLETON__';

// Declaração de tipo para window
declare global {
  interface Window {
    [GLOBAL_KEY]?: AbortManager;
  }
}

export class AbortManager {
  private static instance: AbortManager;
  private abortRefs: Map<string, AbortRef> = new Map();
  private cleanupIntervalId: any = null;
  private storageAvailable: boolean = true;
  
  private constructor() {
    // Verificar se sessionStorage está disponível
    this.checkStorageAvailability();
    
    // Iniciar limpeza automática a cada 30 segundos
    this.startAutoCleanup();
    
    // Log para debug
    console.log('[AbortManager] Nova instância criada', {
      timestamp: new Date().toISOString()
    });
  }
  
  static getInstance(): AbortManager {
    // Primeiro, verificar se existe no window (sobrevive a HMR)
    if (typeof window !== 'undefined' && window[GLOBAL_KEY]) {
      AbortManager.instance = window[GLOBAL_KEY];
      return AbortManager.instance;
    }
    
    // Se não existe, criar nova instância
    if (!AbortManager.instance) {
      AbortManager.instance = new AbortManager();
      
      // Salvar no window para sobreviver a HMR
      if (typeof window !== 'undefined') {
        window[GLOBAL_KEY] = AbortManager.instance;
      }
    }
    
    return AbortManager.instance;
  }

  /**
   * Verifica se sessionStorage está disponível e funcional
   */
  private checkStorageAvailability(): void {
    try {
      const testKey = '__abort_storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      this.storageAvailable = true;
    } catch {
      this.storageAvailable = false;
      terminalLog.warn('[ABORT] sessionStorage não disponível - usando apenas memória');
    }
  }

  /**
   * Inicia limpeza automática de operações antigas
   */
  private startAutoCleanup(): void {
    // Limpar imediatamente operações antigas ao iniciar
    this.cleanupExpiredOperations();
    
    // Executar limpeza a cada 30 segundos
    this.cleanupIntervalId = setInterval(() => {
      this.cleanupExpiredOperations();
    }, 30000);
    
    // Auto-cleanup iniciado - silencioso
  }

  /**
   * Limpa operações antigas do Map (previne memory leak)
   */
  private cleanupExpiredOperations(): void {
    const now = Date.now();
    const ttl = ABORT_TTL.DEFAULT;
    let cleanedCount = 0;

    for (const [operationId, ref] of this.abortRefs.entries()) {
      const age = now - ref.registeredAt;
      if (age > ttl) {
        this.abortRefs.delete(operationId);
        this.clearAbortSignal(operationId);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      // Operações antigas limpas - silencioso
    }
  }

  /**
   * Para a limpeza automática (útil para testes)
   */
  public stopAutoCleanup(): void {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
      // Auto-cleanup parado - silencioso
    }
  }

  /**
   * Registra uma nova operação que pode ser abortada.
   * Retorna AbortController que pode ser verificado durante a operação.
   */
  registerOperation(operationId: string): {
    signal: { aborted: boolean };
    checkAbort: () => boolean;
  } {
    // DEBUG: Log de registro
    sendToTerminal('🔵 [ABORT:REGISTER] Registrando operação', {
      operationId,
      existingCount: this.abortRefs.size,
      existingOps: Array.from(this.abortRefs.keys())
    });
    
    this.abortRefs.set(operationId, { 
      aborted: false,
      registeredAt: Date.now()
    });
    
    // Verificar se já existe sinal de abort no storage (documento fechado)
    const stored = this.readAbortSignal(operationId);
    if (stored) {
      const ref = this.abortRefs.get(operationId)!;
      ref.aborted = true;
      ref.reason = stored.reason;
    }
    
    // DEBUG: Confirmar registro
    sendToTerminal('✅ [ABORT:REGISTER] Operação registrada', {
      operationId,
      newCount: this.abortRefs.size,
      allOps: Array.from(this.abortRefs.keys())
    });
    
    return {
      signal: this.abortRefs.get(operationId)!,
      checkAbort: () => this.isAborted(operationId)
    };
  }

  /**
   * Sinaliza abort para uma operação específica.
   */
  abort(operationId: string, reason: AbortReason, metadata?: Record<string, any>): void {
    const ref = this.abortRefs.get(operationId);
    if (ref) {
      ref.aborted = true;
      ref.reason = reason;
    } else {
      // Operação não está registrada localmente, mas pode estar em outro componente
      // Criar ref para manter no Map
      this.abortRefs.set(operationId, {
        aborted: true,
        reason,
        registeredAt: Date.now()
      });
    }
    
    // Persistir no storage para comunicação entre componentes (se disponível)
    this.writeAbortSignal({
      operationId,
      reason,
      timestamp: Date.now(),
      metadata
    });
    
    sendToTerminal('🛑 [ABORT] Operação abortada', { operationId, reason, metadata });
    terminalLog.warn('[ABORT] Operação abortada', { operationId, reason });
  }

  /**
   * Verifica se uma operação foi abortada.
   */
  isAborted(operationId: string): boolean {
    // Verificar memória primeiro
    const ref = this.abortRefs.get(operationId);
    if (ref?.aborted) {
      return true;
    }
    
    // Verificar storage (caso tenha sido sinalizado de outro componente)
    // Apenas se storage estiver disponível
    if (this.storageAvailable) {
      const stored = this.readAbortSignal(operationId);
      if (stored) {
        // Atualizar memória
        if (ref) {
          ref.aborted = true;
          ref.reason = stored.reason;
        } else {
          // Criar novo ref baseado no storage
          this.abortRefs.set(operationId, {
            aborted: true,
            reason: stored.reason,
            registeredAt: stored.timestamp
          });
        }
        return true;
      }
    }
    
    return false;
  }

  /**
   * Limpa o registro de uma operação (chamar ao finalizar).
   */
  unregisterOperation(operationId: string): void {
    // DEBUG: Log de remoção com stack trace
    const stack = new Error().stack?.split('\n').slice(1, 5).join('\n') || 'no stack';
    sendToTerminal('🔴 [ABORT:UNREGISTER] Removendo operação', {
      operationId,
      hadOperation: this.abortRefs.has(operationId),
      countBefore: this.abortRefs.size,
      stack
    });
    
    this.abortRefs.delete(operationId);
    this.clearAbortSignal(operationId);
    
    sendToTerminal('🔴 [ABORT:UNREGISTER] Operação removida', {
      operationId,
      countAfter: this.abortRefs.size,
      remainingOps: Array.from(this.abortRefs.keys())
    });
  }

  // Métodos privados para storage
  private getAbortKey(operationId: string): string {
    return `abort:signal:${operationId}`;
  }

  private writeAbortSignal(signal: AbortSignal): void {
    if (!this.storageAvailable) {
      sendToTerminal('⚠️ [ABORT] Storage não disponível - sinal apenas em memória', {
        operationId: signal.operationId
      });
      return;
    }

    try {
      storage.set(this.getAbortKey(signal.operationId), signal, {
        area: 'session',
        ttlMs: ABORT_TTL.DEFAULT
      });
    } catch (error) {
      terminalLog.error('[ABORT] Erro ao persistir sinal de abort', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      // Marcar storage como indisponível se falhar
      this.storageAvailable = false;
    }
  }

  private readAbortSignal(operationId: string): AbortSignal | null {
    if (!this.storageAvailable) {
      return null;
    }

    try {
      return storage.get<AbortSignal>(this.getAbortKey(operationId), {
        area: 'session'
      });
    } catch (error) {
      // Silencioso - normal quando não existe
      return null;
    }
  }

  private clearAbortSignal(operationId: string): void {
    if (!this.storageAvailable) {
      return;
    }

    try {
      storage.remove(this.getAbortKey(operationId), 'session');
    } catch {
      // Silencioso - não crítico
    }
  }

  /**
   * Verifica se uma operação está registrada e NÃO foi abortada (operação ativa).
   * Útil para verificar se há salvamento/exportação em progresso.
   */
  public hasOperation(operationId: string): boolean {
    const ref = this.abortRefs.get(operationId);
    // Operação existe E não foi abortada = operação ativa
    return ref !== undefined && !ref.aborted;
  }

  /**
   * Verifica se há alguma operação crítica ativa para um pedido/item.
   * Operações críticas: salvamento e exportação
   */
  public hasCriticalOperationActive(orcId: number | string, oitId: number | string): boolean {
    const saveOpId = `save-${orcId}-${oitId}`;
    // Exportação usa formato diferente: export_${timestamp}_${random}
    // Mas podemos verificar pelo prefixo do operationId
    
    // Verificar salvamento
    if (this.hasOperation(saveOpId)) {
      return true;
    }
    
    // Verificar exportação (qualquer operação que comece com "export")
    for (const [opId, ref] of this.abortRefs.entries()) {
      if (opId.startsWith('export') && !ref.aborted) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Retorna estatísticas do gerenciador (útil para debugging)
   */
  public getStats(): {
    activeOperations: number;
    storageAvailable: boolean;
    operations: Array<{ id: string; aborted: boolean; age: number }>;
  } {
    const now = Date.now();
    const operations = Array.from(this.abortRefs.entries()).map(([id, ref]) => ({
      id,
      aborted: ref.aborted,
      age: now - ref.registeredAt
    }));

    return {
      activeOperations: this.abortRefs.size,
      storageAvailable: this.storageAvailable,
      operations
    };
  }
}

// Exportar instância singleton
export const abortManager = AbortManager.getInstance();

